import React, { useState, useEffect, useCallback } from 'react';
import '../../css/Pages.css';
import EducationHistoryListing from '../../components/Profile/EducationHistory/EducationHistoryListing';
import EducationHistoryForm from '../../components/Profile/EducationHistory/EducationHistoryForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { COUNTRIES_DATA } from '../../data/countriesData';

const EducationHistory = () => {
    const initialForm = { EducationLevel: '', School: '', Major: '', Specialization: '', Country: '', StartDate: '', EndDate: '', StartDateAccuracy: 'date', EndDateAccuracy: 'date' };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const [educationLevels, setEducationLevels] = useState([]);
    const [countries, setCountries] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        alert("Phiên đăng nhập đã hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại!");
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser || !currentUser.Id) {
                handleLogout();
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/educationhistory?userId=${currentUser.Id}`, {
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
                setFilteredData(result);
            }
        } catch (error) {
            console.error("Lỗi fetch data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    const fetchEducationLevels = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/educationlevel`, {
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) setEducationLevels(await response.json());
        } catch (error) {
            console.error("Lỗi tải danh mục trình độ:", error);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchEducationLevels();

        const formattedCountries = COUNTRIES_DATA.map((country) => ({
            id: country.Id,
            name: country.Name,
        }));
        setCountries(formattedCountries);

    }, [fetchData, fetchEducationLevels]);

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        setFilteredData(data.filter(item =>
            item.School?.toLowerCase().includes(query) ||
            item.Major?.toLowerCase().includes(query) ||
            item.EducationLevel?.toLowerCase().includes(query)
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.StartDate && formData.EndDate) {
            const start = new Date(formData.StartDate);
            const end = new Date(formData.EndDate);
            if (end < start) {
                alert("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
                return;
            }
        }

        const method = editId ? 'PUT' : 'POST';
        const currentUser = JSON.parse(localStorage.getItem('user'));

        const payload = {
            ...formData,
            UserId: currentUser.Id
        };

        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/educationhistory`, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                fetchData();
                closeModal();
            } else {
                alert("Có lỗi xảy ra khi lưu dữ liệu!");
            }
        } catch (error) { console.error("Lỗi submit:", error); }
    };

    const handleEdit = (item) => {
        setEditId(item.Id);
        setFormData({
            ...item,
            StartDateAccuracy: item.StartDateAccuracy || 'date',
            EndDateAccuracy: item.EndDateAccuracy || 'date'
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa quá trình đào tạo này?',
            accept: async () => {
                const currentUser = JSON.parse(localStorage.getItem('user'));
                const userId = currentUser ? currentUser.Id : null;

                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/educationhistory?id=${id}&userId=${userId}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });

                if (response.status === 401 || response.status === 403) {
                    handleLogout();
                    return;
                }

                if (response.ok) {
                    fetchData();
                } else {
                    alert("Có lỗi xảy ra khi xóa dữ liệu!");
                }
            }
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData(initialForm);
        setEditId(null);
    };

    return (
        <div className="page-container" style={{ backgroundColor: '#f4f6f9', minHeight: '100vh' }}>
            <div className="page-header mb-4">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ QUÁ TRÌNH ĐÀO TẠO</h2>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={() => setIsModalOpen(true)} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>

                <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <input type="text" className="form-input" placeholder="Tìm kiếm" value={searchQuery} onChange={handleSearch} />
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', right: '15px', top: '12px', color: '#999' }}></i>
                </div>
            </div>

            <EducationHistoryListing data={filteredData} onEdit={handleEdit} onDelete={handleDelete} isLoading={isLoading} />

            <EducationHistoryForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                educationLevels={educationLevels}
                countries={countries}
                isEditing={!!editId}
            />
        </div>
    );
};

export default EducationHistory;